import {
  getV1CompaniesCompanyIdSettlementExportsId,
  postV1CompaniesCompanyIdSettlementExports,
  SettlementExportRendererKey,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { SettlementExport } from '~hooks/useSettlementExports/models';
import connection from '~services/connectionModule';

interface CreateSettlementExportParams {
  companyId: string;
  rendererKey: SettlementExportRendererKey;
  settlementIds: string[];
}

interface GetSettlementExportParams {
  companyId: string;
  settlementExportId: string;
  // For a read whose failure is recoverable, so the caller rethrows instead of toasting.
  shouldSuppressErrorToast?: boolean;
}

export default function useSettlementExports() {
  const createSettlementExport = useCallback(
    async ({ companyId, rendererKey, settlementIds }: CreateSettlementExportParams) => {
      try {
        const response = await postV1CompaniesCompanyIdSettlementExports({
          path: { 'company-id': companyId },
          body: { renderer_key: rendererKey, settlement_ids: settlementIds },
        });

        return SettlementExport.parse(response.data.data);
      } catch (error) {
        // An ineligible settlement rejects the whole batch with a 422 whose message
        // names the problem, so the caller shows it instead of a generic failure.
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        return connection.handleRequestError(
          error,
          t('error_messages.settlement_exports.failed_to_create'),
        );
      }
    },
    [],
  );

  const getSettlementExport = useCallback(
    async ({
      companyId,
      settlementExportId,
      shouldSuppressErrorToast,
    }: GetSettlementExportParams) => {
      try {
        const response = await getV1CompaniesCompanyIdSettlementExportsId({
          path: { 'company-id': companyId, id: settlementExportId },
        });

        return SettlementExport.parse(response.data.data);
      } catch (error) {
        if (shouldSuppressErrorToast) {
          throw error;
        }

        return connection.handleRequestError(
          error,
          t('error_messages.settlement_exports.failed_to_fetch'),
        );
      }
    },
    [],
  );

  return { createSettlementExport, getSettlementExport };
}
